import { assertUnreachable } from '@shared/helpers/function.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';

export const VERSION_MISMATCH_PATH = '/new-version';

/** Poll while {@link VERSION_MISMATCH_PATH} waits for client and server to agree again. */
export const VERSION_MISMATCH_POLL_MS = 10_000;

/**
 * How often a visible tab asks GET /version.
 * The check itself does not reload; a reload happens only when the server
 * version differs from the bundle that is running.
 */
export const VERSION_CHECK_INTERVAL_MS = 60 * 60 * 1000;

/**
 * In-app path only: leading `/`, not protocol-relative (`//`) or a URL (`http`).
 * Used by `/new-version` and Vite `/not-found` recovery so query `path` cannot
 * open an external origin.
 */
export const isSafeInAppReturnPath = (returnPath: string): boolean => {
  if (!returnPath.startsWith('/') || returnPath.startsWith('//')) return false;
  const lower = returnPath.toLowerCase();
  return !lower.includes('http:') && !lower.includes('https:');
};

/**
 * Return URL stored on {@link VERSION_MISMATCH_PATH} (`returnPath` + `returnSearch`).
 * Missing or unsafe `returnPath` goes to `/`. `returnSearch` is kept only when it
 * starts with `?`.
 */
export const getVersionMismatchReturnTo = (search: string): string => {
  const params = new URLSearchParams(search);
  const returnPath = params.get('returnPath');
  if (!returnPath || !isSafeInAppReturnPath(returnPath)) return '/';
  const returnSearch = params.get('returnSearch') ?? '';
  if (returnSearch && !returnSearch.startsWith('?')) return returnPath;
  return `${returnPath}${returnSearch}`;
};

export type AppVersionCheckAction =
  | { type: 'none' }
  | { type: 'synced' }
  | { type: 'recover'; returnTo: string }
  | { type: 'reload'; serverVersion: string };

type AppVersionCheckInput = {
  pathname: string;
  search: string;
  localAppVersion: string;
  serverVersion: string | undefined;
};

export type AppVersionCheckSnapshot = {
  pathname: string;
  search: string;
  localAppVersion: string;
};

export type ResolvedAppVersionCheckAction =
  { type: 'stale' } | AppVersionCheckAction;

/**
 * Decide whether to stay, return from `/new-version`, or force a mismatch reload.
 * Any difference reloads, including a patch (`1.71.0` → `1.71.1`). The API
 * itself only errors when major or minor differs.
 * Matching versions on `/new-version` mean rebase/deploy finished — navigate back.
 * Mismatch off that page reloads onto it. Missing server version is a no-op.
 */
export const getAppVersionCheckAction = ({
  pathname,
  search,
  localAppVersion,
  serverVersion,
}: AppVersionCheckInput): AppVersionCheckAction => {
  if (!serverVersion) return { type: 'none' };

  if (serverVersion === localAppVersion) {
    if (pathname === VERSION_MISMATCH_PATH) {
      return {
        type: 'recover',
        returnTo: getVersionMismatchReturnTo(search),
      };
    }
    return { type: 'synced' };
  }

  if (pathname === VERSION_MISMATCH_PATH) return { type: 'none' };
  return { type: 'reload', serverVersion };
};

export type VersionCheckScheduleInput = {
  isVersionMismatchPage: boolean;
  documentVisible: boolean;
};

/**
 * Delay until the next proactive `/version` request.
 * On {@link VERSION_MISMATCH_PATH}, always the short poll — including a hidden tab,
 * so a background rebase can still recover.
 * A visible tab asks every {@link VERSION_CHECK_INTERVAL_MS}. A hidden tab waits
 * until it is shown again; that return check is immediate and is not this delay.
 * Returns null when no timer should run.
 */
export const getNextVersionCheckDelayMs = ({
  isVersionMismatchPage,
  documentVisible,
}: VersionCheckScheduleInput): number | null => {
  if (isVersionMismatchPage) return VERSION_MISMATCH_POLL_MS;
  if (!documentVisible) return null;
  return VERSION_CHECK_INTERVAL_MS;
};

export type AppVersionCheckHandlers = {
  onSynced: () => void;
  onRecover: (returnTo: string) => void;
  /** `serverVersion` is the version GET /version just returned. */
  onReload: (serverVersion: string) => void;
};

type ApplyAppVersionCheckInput = {
  apiUrl: string;
  localAppVersion: string;
  pathname: string;
  search: string;
  getCurrentSnapshot: () => AppVersionCheckSnapshot;
  handlers: AppVersionCheckHandlers;
  fetchFn?: typeof fetch;
};

/**
 * Fetch the server version and reload, recover, or no-op.
 * Drops the result when path, query, or client version changed during the request.
 * A failed fetch is a no-op so a restart or branch switch does not redirect.
 */
export const applyAppVersionCheck = async ({
  apiUrl,
  localAppVersion,
  pathname,
  search,
  getCurrentSnapshot,
  handlers,
  fetchFn,
}: ApplyAppVersionCheckInput): Promise<void> => {
  const started: AppVersionCheckSnapshot = {
    pathname,
    search,
    localAppVersion,
  };
  try {
    const serverVersion = await fetchServerAppVersion(apiUrl, fetchFn);
    const action = resolveAppVersionCheckAction(
      started,
      getCurrentSnapshot(),
      serverVersion,
    );

    switch (action.type) {
      case 'stale':
      case 'none':
        return;
      case 'synced':
        handlers.onSynced();
        return;
      case 'recover':
        handlers.onRecover(action.returnTo);
        return;
      case 'reload':
        handlers.onReload(action.serverVersion);
        return;
      default:
        assertUnreachable(action);
    }
  } catch (e) {
    console.info('version check failed', e);
  }
};

/**
 * Drop the result when the live route, query, or client version changed during
 * the request. Uses the live snapshot so recover follows the current return URL.
 */
export const resolveAppVersionCheckAction = (
  started: AppVersionCheckSnapshot,
  current: AppVersionCheckSnapshot,
  serverVersion: string | undefined,
): ResolvedAppVersionCheckAction => {
  if (!isCurrentAppVersionCheck(started, current)) return { type: 'stale' };
  return getAppVersionCheckAction({
    pathname: current.pathname,
    search: current.search,
    localAppVersion: current.localAppVersion,
    serverVersion,
  });
};

/**
 * True when the in-flight check still matches the live route, query, and client
 * version. Stale results must not redirect after a rebase/HMR already synced.
 */
export const isCurrentAppVersionCheck = (
  started: AppVersionCheckSnapshot,
  current: AppVersionCheckSnapshot,
): boolean =>
  started.pathname === current.pathname &&
  started.search === current.search &&
  started.localAppVersion === current.localAppVersion;

/**
 * Read the server package version from GET /version.
 * Returns undefined when the API is unreachable (local restart / branch switch)
 * so callers can no-op instead of throwing.
 */
export const fetchServerAppVersion = async (
  apiUrl: string,
  fetchFn: typeof fetch = fetch,
): Promise<string | undefined> => {
  try {
    const resp = await fetchFn(`${apiUrl}/version?r=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!resp.ok) return undefined;

    const data: unknown = await resp.json();
    if (!guards.is.object(data) || !guards.is.keyOf(data, 'version')) {
      return undefined;
    }
    if (!guards.is.string(data.version)) return undefined;
    return data.version;
  } catch {
    return undefined;
  }
};
